import { defineStore } from "pinia";
import { ref, computed, readonly } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { DEFAULT_LANGUAGE_TAG, normalizeLocale } from "@/shared/utils/localeUtils";
export const usePaymentStore = defineStore("payment", () => {
    // State
    const organizationInfo = ref(null);
    const paymentDetails = ref(null);
    const paymentSession = ref(null);
    const paymentId = ref(null);
    const isLoading = ref(false);
    const isAdyenReady = ref(false);
    const error = ref(null);
    // Actions
    const setOrganizationInfo = (info) => {
        organizationInfo.value = info;
        logger.debug("Payment store: Organization info updated", info);
    };
    const setPaymentSession = (session) => {
        paymentSession.value = session;
        logger.debug("Payment store: Payment session updated", session);
    };
    const setPaymentDetails = (details) => {
        paymentDetails.value = details;
        logger.debug("Payment store: Payment details updated", details);
    };
    const setPaymentId = (id) => {
        paymentId.value = id;
        logger.debug("Payment store: Payment ID updated", id);
    };
    const setLoading = (loading) => {
        isLoading.value = loading;
    };
    const setAdyenReady = (ready) => {
        isAdyenReady.value = ready;
        logger.debug("Payment store: Adyen ready state changed", ready);
    };
    const setError = (errorMessage) => {
        error.value = errorMessage;
        if (errorMessage) {
            logger.debug("Payment store: Error set", errorMessage);
        }
    };
    const clearError = () => {
        error.value = null;
    };
    const reset = () => {
        organizationInfo.value = null;
        paymentDetails.value = null;
        paymentSession.value = null;
        paymentId.value = null;
        isLoading.value = false;
        isAdyenReady.value = false;
        error.value = null;
        logger.debug("Payment store: Reset to initial state");
    };
    // Getters
    const hasOrganizationInfo = computed(() => organizationInfo.value !== null);
    const hasPaymentSession = computed(() => paymentSession.value !== null);
    const isInitialized = computed(() => hasOrganizationInfo.value && hasPaymentSession.value);
    const canShowPayment = computed(() => isInitialized.value && isAdyenReady.value && !isLoading.value);
    // Get locale and countryCode from PaymentDetails
    const paymentLocale = computed(() => paymentDetails.value?.locale || organizationInfo.value?.locale || DEFAULT_LANGUAGE_TAG);
    const paymentCountryCode = computed(() => paymentDetails.value?.countryCode || "FR");
    // Get normalized locale for i18n (matches with available translations)
    const normalizedPaymentLocale = computed(() => normalizeLocale(paymentLocale.value));
    return {
        // State (readonly)
        organizationInfo: readonly(organizationInfo),
        paymentDetails: readonly(paymentDetails),
        paymentSession: readonly(paymentSession),
        paymentId: readonly(paymentId),
        isLoading: readonly(isLoading),
        isAdyenReady: readonly(isAdyenReady),
        error: readonly(error),
        // Actions
        setOrganizationInfo,
        setPaymentDetails,
        setPaymentSession,
        setPaymentId,
        setLoading,
        setAdyenReady,
        setError,
        clearError,
        reset,
        // Getters
        hasOrganizationInfo,
        hasPaymentSession,
        isInitialized,
        canShowPayment,
        paymentLocale,
        paymentCountryCode,
        normalizedPaymentLocale,
    };
});
